import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import useApi from '../../hooks/useApi';
import { useAuth } from '../../auth/googleDomainAuth.jsx';
import DetailPanelAppSheetTab from '../../components/detailPanel/appsheet/DetailPanelAppSheetTab';
import { scheduleMrtFilterPopoverOpen } from '../../utils/mrtFilterPopover';
import { fuzzyFilterRows } from './appsheetAccountsBrowser/fuzzySearch';
import {
  buildCombinedSelectionRowsForTable,
  buildProgramCardModel,
  flattenEnrollmentsForBac,
} from './appsheetAccountsBrowser/dpeViewModel';
import {
  AppsheetAccountsAccountTabPanel,
  AppsheetAccountsBuySellsTabPanel,
  AppsheetAccountsDpeTabPanel,
  AppsheetAccountsHistoryTabPanel,
} from './WipAppsheetAccountsBrowserPage.jsx';
import {
  buildColumns,
  getDefaultPinning,
  getOrderItemAccountId,
  getOrderItemAccountName,
  getOrderItemBac,
  getOrderItemDiBrandName,
  getOrderItemId,
  loadJson,
  pairedSourceGenerationState,
  rowsForPairedGeneration,
  saveJson,
  sourceSnapshotMarker,
} from './appsheetOrderItemsBrowser/helpers.jsx';

const ORDER_ITEMS_TABLE_NAME = 'SF_GM OrderItems';
const ACCOUNTS_TABLE_NAME = 'SF_GM Salesforce Accounts';
const ROW_LOAD_BATCH = 1000;
const MAX_ROWS_FOR_WIP = 25000;

const WipAppsheetOrderItemsBrowserPage = () => {
  const location = useLocation();
  const { user } = useAuth() || {};
  const { useAllEnrollmentData, useBuySells } = useApi();
  const { data: allEnrollmentData, isLoading: loadingDpeData, error: dpeError } = useAllEnrollmentData();
  const { data: buySellData, isLoading: loadingBuySells, error: buySellsError } = useBuySells();
  const userKey = user?.email ? String(user.email).toLowerCase() : 'guest';

  const tableColumnStorageKey = `wipAppsheetOrderItemsColumns:${userKey}`;
  const tableStateStorageKey = `wipAppsheetOrderItemsTableState:${userKey}`;

  const [tableMeta, setTableMeta] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [accountsMeta, setAccountsMeta] = useState(null);
  const [accountsRows, setAccountsRows] = useState([]);
  const [orderItemsSourceMarker, setOrderItemsSourceMarker] = useState(undefined);
  const [accountsSourceMarker, setAccountsSourceMarker] = useState(undefined);
  const [isLoadingRows, setIsLoadingRows] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading AppSheet order items...');
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('orderItems');
  const [pendingFilterColumnId, setPendingFilterColumnId] = useState(null);
  const validDrawerTabs = useMemo(() => new Set(['account', 'dpe', 'orderItems', 'buySells', 'history']), []);
  const activeDrawerTab = validDrawerTabs.has(drawerTab) ? drawerTab : 'orderItems';

  const savedColumnState = useMemo(
    () => loadJson(tableColumnStorageKey, { visibility: {}, ordering: [], pinning: { left: [], right: [] } }),
    [tableColumnStorageKey]
  );
  const savedTableState = useMemo(
    () => loadJson(tableStateStorageKey, { columnFilters: [], sorting: [] }),
    [tableStateStorageKey]
  );

  const [columnVisibility, setColumnVisibility] = useState(savedColumnState.visibility || {});
  const [columnOrder, setColumnOrder] = useState(Array.isArray(savedColumnState.ordering) ? savedColumnState.ordering : []);
  const [columnPinning, setColumnPinning] = useState(savedColumnState.pinning || { left: [], right: [] });
  const [columnFilters, setColumnFilters] = useState(
    Array.isArray(savedTableState.columnFilters) ? savedTableState.columnFilters : []
  );
  const [sorting, setSorting] = useState(Array.isArray(savedTableState.sorting) ? savedTableState.sorting : []);

  const isWipRoute = String(location?.pathname || '').startsWith('/__wip/');

  useEffect(() => {
    saveJson(tableColumnStorageKey, {
      visibility: columnVisibility,
      ordering: columnOrder,
      pinning: columnPinning,
    });
  }, [tableColumnStorageKey, columnVisibility, columnOrder, columnPinning]);

  useEffect(() => {
    saveJson(tableStateStorageKey, { columnFilters, sorting });
  }, [tableStateStorageKey, columnFilters, sorting]);

  useEffect(() => {
    let active = true;

    const loadAllRows = async () => {
      setIsLoadingRows(true);
      setLoadError('');
      setLoadingMessage('Loading AppSheet order items table metadata...');

      try {
        const { data: tablesData } = await axios.get('/api/appsheet/tables');
        const tables = Array.isArray(tablesData?.tables) ? tablesData.tables : [];
        const orderItemsTable = tables.find((table) => table?.name === ORDER_ITEMS_TABLE_NAME);

        if (!orderItemsTable) {
          throw new Error(`AppSheet table "${ORDER_ITEMS_TABLE_NAME}" is not configured.`);
        }
        if (!active) return;

        setTableMeta(orderItemsTable);
        if (!savedColumnState?.pinning?.left?.length && !savedColumnState?.pinning?.right?.length) {
          setColumnPinning(getDefaultPinning(orderItemsTable.fields || []));
        }

        let cursor;
        let hasMore = true;
        let loadedRows = [];

        while (hasMore) {
          setLoadingMessage(`Loading AppSheet order items (${loadedRows.length.toLocaleString()} loaded)...`);
          const { data: pageData } = await axios.post(
            `/api/appsheet/tables/${encodeURIComponent(orderItemsTable.id)}/rows/query`,
            {
              limit: ROW_LOAD_BATCH,
              cursorId: cursor?.id,
              cursorGenerationId: cursor?.generationId,
              includeTotal: false,
            }
          );
          const pageRows = Array.isArray(pageData?.rows) ? pageData.rows : [];
          if (loadedRows.length === 0) {
            setOrderItemsSourceMarker(sourceSnapshotMarker(pageData?.table));
          }
          loadedRows = loadedRows.concat(pageRows);

          if (loadedRows.length > MAX_ROWS_FOR_WIP) {
            throw new Error(
              `WIP browser row cap exceeded (${MAX_ROWS_FOR_WIP.toLocaleString()}). Refine upstream data before loading all rows.`
            );
          }

          const nextCursor = pageData?.nextCursor;
          if (!nextCursor?.id || pageRows.length === 0) hasMore = false;
          else cursor = nextCursor;
        }

        if (!active) return;
        setAllRows(
          loadedRows.map((row, index) => ({
            ...row,
            __rowId: index + 1,
            __searchAccountName: getOrderItemAccountName(row),
            __searchBac: getOrderItemBac(row),
            __searchId: getOrderItemId(row),
            __searchDiBrandName: getOrderItemDiBrandName(row),
          }))
        );
      } catch (error) {
        if (!active) return;
        setLoadError(error?.response?.data?.error || error?.message || 'Failed to load AppSheet order items.');
      } finally {
        if (active) {
          setIsLoadingRows(false);
          setLoadingMessage('');
        }
      }
    };

    void loadAllRows();
    return () => {
      active = false;
    };
  }, [savedColumnState?.pinning?.left?.length, savedColumnState?.pinning?.right?.length]);

  useEffect(() => {
    let active = true;

    const loadAccountsRows = async () => {
      try {
        const { data: tablesData } = await axios.get('/api/appsheet/tables');
        const tables = Array.isArray(tablesData?.tables) ? tablesData.tables : [];
        const accountsTable = tables.find((table) => table?.name === ACCOUNTS_TABLE_NAME);
        if (!accountsTable) throw new Error(`AppSheet table "${ACCOUNTS_TABLE_NAME}" is not configured.`);
        if (!active) return;
        setAccountsMeta(accountsTable);

        let cursor;
        let hasMore = true;
        let loadedRows = [];
        while (hasMore) {
          const { data: pageData } = await axios.post(
            `/api/appsheet/tables/${encodeURIComponent(accountsTable.id)}/rows/query`,
            {
              limit: ROW_LOAD_BATCH,
              cursorId: cursor?.id,
              cursorGenerationId: cursor?.generationId,
              includeTotal: false,
            }
          );
          const pageRows = Array.isArray(pageData?.rows) ? pageData.rows : [];
          if (loadedRows.length === 0) {
            setAccountsSourceMarker(sourceSnapshotMarker(pageData?.table));
          }
          loadedRows = loadedRows.concat(pageRows);
          const nextCursor = pageData?.nextCursor;
          if (!nextCursor?.id || pageRows.length === 0) hasMore = false;
          else cursor = nextCursor;
        }
        if (!active) return;
        setAccountsRows(loadedRows);
      } catch (error) {
        if (!active) return;
        setLoadError(error?.response?.data?.error || error?.message || 'Failed to load AppSheet Accounts.');
      }
    };

    void loadAccountsRows();
    return () => {
      active = false;
    };
  }, []);

  const pairedGenerationState = pairedSourceGenerationState(accountsSourceMarker, orderItemsSourceMarker);
  const pairedGenerationReady = pairedGenerationState !== 'pending';
  const pairedGenerationMatches = pairedGenerationState === 'match';
  const safeAccountsRows = useMemo(
    () => rowsForPairedGeneration(pairedGenerationState, accountsRows),
    [accountsRows, pairedGenerationState]
  );

  useEffect(() => {
    if (!pairedGenerationReady || pairedGenerationMatches) return;
    setLoadError('Accounts and OrderItems were read from different source generations. Reload to retry safely.');
  }, [pairedGenerationMatches, pairedGenerationReady]);

  const searchFieldNames = useMemo(
    () => ['__searchAccountName', '__searchBac', '__searchId', '__searchDiBrandName'],
    []
  );
  const filteredRows = useMemo(
    () => fuzzyFilterRows(allRows, searchQuery, searchFieldNames),
    [allRows, searchQuery, searchFieldNames]
  );
  const tableColumns = useMemo(() => buildColumns(tableMeta?.fields || []), [tableMeta?.fields]);

  const searchSuggestions = useMemo(() => {
    const raw = String(searchQuery || '').trim().toLowerCase();
    if (!raw) return [];
    const byLabel = new Map();
    for (const row of allRows) {
      const items = [
        { label: `${row.__searchAccountName} · Account`, value: row.__searchAccountName },
        { label: `${row.__searchBac} · BAC`, value: row.__searchBac },
        { label: `${row.__searchId} · OrderItem ID`, value: row.__searchId },
        { label: `${row.__searchDiBrandName} · DI Brand`, value: row.__searchDiBrandName },
      ].filter((item) => item.value);
      for (const item of items) {
        const needle = item.label.toLowerCase();
        const value = item.value.toLowerCase();
        if (!needle.includes(raw) && !value.includes(raw)) continue;
        if (!byLabel.has(item.label)) byLabel.set(item.label, item);
      }
      if (byLabel.size >= 12) break;
    }
    return Array.from(byLabel.values()).slice(0, 12);
  }, [allRows, searchQuery]);

  const selectedAccountId = getOrderItemAccountId(selectedRow);
  const selectedAccountName = getOrderItemAccountName(selectedRow);

  const accountRowsById = useMemo(() => {
    const map = new Map();
    safeAccountsRows.forEach((row) => {
      const accountId = String(row?.Account_ID__c || '').trim().toLowerCase();
      if (accountId) map.set(accountId, row);
    });
    return map;
  }, [safeAccountsRows]);

  const selectedAccountRow = useMemo(() => {
    const key = String(selectedAccountId || '').trim().toLowerCase();
    if (!key) return null;
    return accountRowsById.get(key) || null;
  }, [accountRowsById, selectedAccountId]);

  const selectedBac = String(selectedAccountRow?.BAC_Code__c || getOrderItemBac(selectedRow) || '').trim();
  const hasSelectedBac = Boolean(selectedBac);

  const dealerEnrollments = useMemo(
    () => flattenEnrollmentsForBac(allEnrollmentData, hasSelectedBac ? selectedBac : ''),
    [allEnrollmentData, hasSelectedBac, selectedBac]
  );
  const programCardModels = useMemo(() => dealerEnrollments.map((row) => buildProgramCardModel(row)), [dealerEnrollments]);
  const combinedSelectionRowsForTable = useMemo(
    () => buildCombinedSelectionRowsForTable(dealerEnrollments),
    [dealerEnrollments]
  );

  const relatedBuySells = useMemo(() => {
    const rows = Array.isArray(buySellData) ? buySellData : [];
    if (!selectedAccountId) return [];
    const target = selectedAccountId.toLowerCase();
    return rows.filter((row) => {
      const selling = String(row?.accountId || '').trim().toLowerCase();
      const buying = String(row?.buySellAccountId || '').trim().toLowerCase();
      return selling === target || buying === target;
    });
  }, [buySellData, selectedAccountId]);

  const buySellColumns = useMemo(
    () => [
      { field: 'accountName', headerName: 'Account Name', flex: 1.2, minWidth: 220 },
      { field: 'accountId', headerName: 'Selling Account ID', flex: 1, minWidth: 190 },
      { field: 'buySellAccountId', headerName: 'Buying Account ID', flex: 1, minWidth: 190 },
      { field: 'buySellStage', headerName: 'Stage', width: 120 },
      { field: 'stageDate', headerName: 'Stage Date', width: 130 },
      { field: 'bac', headerName: 'BAC', width: 110 },
      { field: 'lastEmailSentAt', headerName: 'Last Alert', minWidth: 180, flex: 1 },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns: tableColumns,
    data: filteredRows,
    localization: MRT_Localization_EN,
    getRowId: (row) => String(row?.__rowId ?? row?.Id ?? row?.id ?? Math.random()),
    enableRowSelection: false,
    enableColumnOrdering: true,
    enableColumnDragging: false,
    enableColumnResizing: true,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enablePagination: true,
    enableGlobalFilter: false,
    enableColumnFilters: true,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableSorting: true,
    enableRowVirtualization: true,
    state: { columnFilters, sorting, columnVisibility, columnOrder, columnPinning, isLoading: isLoadingRows },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    initialState: { density: 'compact', pagination: { pageIndex: 0, pageSize: 100 }, columnPinning },
    muiPaginationProps: { rowsPerPageOptions: [25, 50, 100, 250] },
    renderColumnActionsMenuItems: ({ column, closeMenu, internalColumnMenuItems, table }) => {
      if (!column.getCanFilter?.()) return internalColumnMenuItems;
      const addFilterItem = (
        <MenuItem
          key="add-filter"
          onClick={() => {
            setPendingFilterColumnId(column.id);
            closeMenu?.();
          }}
        >
          <FilterAltIcon fontSize="small" style={{ marginRight: 8 }} />
          Add Filter
        </MenuItem>
      );
      const internalItems = Array.isArray(internalColumnMenuItems)
        ? [...internalColumnMenuItems]
        : internalColumnMenuItems
          ? [internalColumnMenuItems]
          : [];
      const clearFilterLabel = (table?.options?.localization?.clearFilter || 'Clear Filter').toLowerCase();
      const clearFilterIndex = internalItems.findIndex((item) =>
        String(item?.props?.label || item?.props?.children || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase()
          .includes(clearFilterLabel)
      );
      if (clearFilterIndex === -1) return [addFilterItem, ...internalItems];
      internalItems.splice(clearFilterIndex, 0, addFilterItem);
      return internalItems;
    },
    renderToolbarInternalActions: ({ table }) => {
      const {
        columnFilterDisplayMode,
        enableColumnFilters,
        enableColumnOrdering,
        enableColumnPinning,
        enableDensityToggle,
        enableFilters,
        enableFullScreenToggle,
        enableGlobalFilter,
        enableHiding,
        initialState,
      } = table.options;
      const hasActiveFilters = (table.getState().columnFilters?.length || 0) > 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {enableFilters && enableGlobalFilter && !(initialState?.showGlobalFilter) ? (
            <MRT_ToggleGlobalFilterButton table={table} />
          ) : null}
          <Tooltip title="Reset filters">
            <span>
              <IconButton size="small" onClick={() => setColumnFilters([])} disabled={!hasActiveFilters}>
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {enableFilters && enableColumnFilters && columnFilterDisplayMode !== 'popover' ? (
            <MRT_ToggleFiltersButton table={table} />
          ) : null}
          {(enableHiding || enableColumnOrdering || enableColumnPinning) ? (
            <MRT_ShowHideColumnsButton table={table} />
          ) : null}
          {enableDensityToggle ? <MRT_ToggleDensePaddingButton table={table} /> : null}
          {enableFullScreenToggle ? <MRT_ToggleFullScreenButton table={table} /> : null}
        </Box>
      );
    },
    muiTableBodyRowProps: ({ row }) => ({
      hover: true,
      onClick: (event) => {
        const target = event?.target;
        if (target?.closest?.('button, a, input, textarea, [role="button"]')) return;
        setSelectedRow(row.original);
        setDrawerTab('orderItems');
        setDrawerOpen(true);
      },
      sx: { cursor: 'pointer' },
    }),
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 280px)' } },
  });

  useEffect(() => {
    if (!pendingFilterColumnId || !table) return;
    return scheduleMrtFilterPopoverOpen({
      table,
      columnId: pendingFilterColumnId,
      buttonSelector: 'button[aria-label*="Filter by"]',
      onComplete: () => setPendingFilterColumnId(null),
    });
  }, [pendingFilterColumnId, table]);

  return (
    <Box sx={{ maxWidth: 1660, mx: 'auto', py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2 } }}>
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 4,
          p: { xs: 2, sm: 3 },
          borderColor: 'rgba(10,42,65,0.22)',
          background:
            'linear-gradient(120deg, rgba(10,42,65,0.18), rgba(255,255,255,0) 47%), linear-gradient(300deg, rgba(1,85,77,0.14), rgba(255,255,255,0) 42%)',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
          {isWipRoute ? <Chip label="WIP" color="warning" size="small" /> : null}
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            AppSheet OrderItems Browser
          </Typography>
        </Box>
        <Typography sx={{ color: 'text.secondary', mb: 2.25, maxWidth: 920 }}>
          Search and investigate <strong>{ORDER_ITEMS_TABLE_NAME}</strong> by account name, BAC, order item ID, and DI brand.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Cached rows: ${allRows.length.toLocaleString()}`} color="primary" variant="outlined" size="small" />
          <Chip label={`Fields: ${(tableMeta?.fields || []).length}`} variant="outlined" size="small" />
          <Chip label={`Result rows: ${filteredRows.length.toLocaleString()}`} variant="outlined" size="small" />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            freeSolo
            options={searchSuggestions}
            value={null}
            inputValue={searchQuery}
            onInputChange={(_event, value) => setSearchQuery(value || '')}
            onChange={(_event, option) => {
              if (!option) return;
              if (typeof option === 'string') setSearchQuery(option);
              else setSearchQuery(option.value || option.label || '');
            }}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Search OrderItems" placeholder="Account name, BAC, ID, DI brand..." />
            )}
            sx={{ minWidth: { xs: '100%', sm: 460 } }}
          />
          <Chip
            label={`Rows: ${filteredRows.length.toLocaleString()} / ${allRows.length.toLocaleString()}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      {isLoadingRows ? (
        <Paper variant="outlined" sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {loadingMessage || 'Loading rows...'}
          </Typography>
        </Paper>
      ) : (
        <MaterialReactTable table={table} />
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 1020 }, maxWidth: '100vw' } }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Tabs value={activeDrawerTab} onChange={(_event, value) => setDrawerTab(value)}>
              <Tab
                value="account"
                label="Account"
                disabled={!pairedGenerationMatches}
                icon={<AccountCircleOutlinedIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab value="dpe" label="DPE" icon={<QueryStatsOutlinedIcon fontSize="small" />} iconPosition="start" />
              <Tab
                value="orderItems"
                label="OrderItems"
                icon={<ShoppingCartOutlinedIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                value="buySells"
                label="Buy/Sells"
                icon={<CompareArrowsOutlinedIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab value="history" label="History" icon={<HistoryOutlinedIcon fontSize="small" />} iconPosition="start" />
            </Tabs>
            <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close details">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {activeDrawerTab === 'orderItems' ? (
              selectedBac ? (
                <DetailPanelAppSheetTab
                  bac={selectedBac}
                  userEmail={user?.email || null}
                  orderItemsFirst
                  initialSelectedAccountId={selectedAccountId || null}
                />
              ) : (
                <Alert severity="info">OrderItems context needs BAC on the selected row.</Alert>
              )
            ) : null}
            {activeDrawerTab === 'account' ? (
              pairedGenerationMatches ? (
                <AppsheetAccountsAccountTabPanel
                  selectedRow={selectedAccountRow}
                  allRows={safeAccountsRows}
                  tableFields={accountsMeta?.fields || []}
                  selectedAccountId={selectedAccountId}
                  userKey={userKey}
                  onSelectRow={(row) => {
                    const rowAccountId = String(row?.Account_ID__c || '').trim().toLowerCase();
                    if (!rowAccountId) return;
                    const linkedOrderItem = allRows.find(
                      (item) => String(getOrderItemAccountId(item) || '').trim().toLowerCase() === rowAccountId
                    );
                    if (linkedOrderItem) setSelectedRow(linkedOrderItem);
                  }}
                />
              ) : (
                <Alert severity="warning">Account details require matching Accounts and OrderItems generations.</Alert>
              )
            ) : null}
            {activeDrawerTab === 'dpe' ? (
              <AppsheetAccountsDpeTabPanel
                hasSelectedBac={hasSelectedBac}
                selectedBac={selectedBac}
                dpeError={dpeError}
                loadingDpeData={loadingDpeData}
                programCardModels={programCardModels}
                combinedSelectionRowsForTable={combinedSelectionRowsForTable}
                userEmail={user?.email || null}
              />
            ) : null}
            {activeDrawerTab === 'buySells' ? (
              <AppsheetAccountsBuySellsTabPanel
                buySellsError={buySellsError}
                loadingBuySells={loadingBuySells}
                relatedBuySells={relatedBuySells}
                buySellColumns={buySellColumns}
                selectedAccountId={selectedAccountId}
              />
            ) : null}
            {activeDrawerTab === 'history' ? (
              <AppsheetAccountsHistoryTabPanel
                bac={selectedBac}
                accountId={selectedAccountId}
                accountName={selectedAccountName}
                rowKey={getOrderItemId(selectedRow)}
              />
            ) : null}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default WipAppsheetOrderItemsBrowserPage;
